import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import SyringeSelector from './components/SyringeSelector';
import PeptideInput from './components/PeptideInput';
import WaterVolumeInput from './components/WaterVolumeInput';
import DoseInput from './components/DoseInput';
import CalculationResults from './components/CalculationResults';
import SaveCalculationModal from './components/SaveCalculationModal';


const PeptideCalculator = () => {
  const [syringeVolume, setSyringeVolume] = useState(100);
  const [peptides, setPeptides] = useState([{ id: 1, name: 'Peptide 1', quantity: '' }]);
  const [waterVolume, setWaterVolume] = useState({ value: '', unit: 'ml' });
  const [peptideDose, setPeptideDose] = useState({ value: '', unit: 'mg', peptideIndex: 0 });
  const [calculationResult, setCalculationResult] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const addPeptide = () => {
    if (peptides?.length < 3) {
      setPeptides([...peptides, { id: peptides?.length + 1, name: `Peptide ${peptides?.length + 1}`, quantity: '' }]);
    }
  };

  const removePeptide = (id) => {
    if (peptides?.length > 1) {
      setPeptides(peptides?.filter(p => p?.id !== id));
    }
  };

  const updatePeptide = (id, quantity) => {
    setPeptides(peptides?.map(p => p?.id === id ? { ...p, quantity } : p));
  };

  const calculateDosage = () => {
    const selectedPeptide = peptides?.[peptideDose?.peptideIndex];
    if (!selectedPeptide?.quantity || !waterVolume?.value || !peptideDose?.value) {
      return;
    }

    const peptideQty = parseFloat(selectedPeptide?.quantity);
    let waterVol = parseFloat(waterVolume?.value);
    
    if (waterVolume?.unit === 'IU') {
      waterVol = waterVol / syringeVolume;
    }

    let doseValue = parseFloat(peptideDose?.value);
    if (peptideDose?.unit === 'mcg') {
      doseValue = doseValue / 1000;
    }

    const concentration = peptideQty / waterVol;
    const unitsPerDose = (doseValue / concentration) * syringeVolume;
    const totalDoses = peptideQty / doseValue;
    const dosesInMl = doseValue / concentration;

    setCalculationResult({
      unitsPerDose: unitsPerDose?.toFixed(2),
      concentration: concentration?.toFixed(2),
      totalDoses: Math.floor(totalDoses),
      dosesInMl: dosesInMl?.toFixed(2),
      doseValue: doseValue,
      doseUnit: peptideDose?.unit,
      peptideName: selectedPeptide?.name,
      syringeVolume
    });
  };

  const handleSaveCalculation = (title, notes) => {
    const savedCalc = {
      id: Date.now(),
      title: title || `Calculation ${new Date()?.toLocaleDateString()}`,
      notes,
      date: new Date()?.toISOString(),
      syringeVolume,
      peptides: peptides?.filter(p => p?.quantity),
      waterVolume,
      peptideDose,
      result: calculationResult
    };

    const existing = JSON.parse(localStorage?.getItem('savedCalculations') || '[]');
    localStorage?.setItem('savedCalculations', JSON.stringify([savedCalc, ...existing]));
    setShowSaveModal(false);
  };

  return (
    <>
      <Helmet>
        <title>Peptide Calculator - INF LABS</title>
        <meta name="description" content="Calculate precise peptide dosages with our advanced calculator" />
      </Helmet>

      <div className="min-h-screen bg-[var(--color-background)]">
        <Header />
        
        <main className="pt-[100px] pb-12 px-4 md:px-6 lg:px-8">
          <div className="max-w-[900px] mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Peptide Calculator
              </h1>
              <p className="text-lg text-muted-foreground">
                Calculate precise dosages for peptide reconstitution and administration
              </p>
            </div>

            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-card border border-border">
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  Select the Total Volume of your Syringe
                </h2>
                <SyringeSelector value={syringeVolume} onChange={setSyringeVolume} />
              </div>

              <div className="p-6 rounded-xl bg-card border border-border">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-foreground">
                    Enter the Quantity of Peptide
                  </h2>
                  {peptides?.length < 3 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addPeptide}
                      iconName="Plus"
                    >
                      ADD PEPTIDE
                    </Button>
                  )}
                </div>
                <div className="space-y-4">
                  {peptides?.map((peptide, index) => (
                    <PeptideInput
                      key={peptide?.id}
                      peptide={peptide}
                      onUpdate={updatePeptide}
                      onRemove={removePeptide}
                      showRemove={peptides?.length > 1}
                    />
                  ))}
                </div>
              </div>

              <div className="p-6 rounded-xl bg-card border border-border">
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  Enter the Quantity of Bacteriostatic Water
                </h2>
                <WaterVolumeInput value={waterVolume} onChange={setWaterVolume} />
              </div>

              <div className="p-6 rounded-xl bg-card border border-border">
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  Enter the Quantity of Peptide in each dose
                </h2>
                <DoseInput
                  value={peptideDose}
                  onChange={setPeptideDose}
                  peptides={peptides}
                />
              </div>

              <Button
                fullWidth
                size="lg"
                onClick={calculateDosage}
                className="bg-gradient-to-r from-primary to-secondary hover:opacity-90"
              >
                Formulate
              </Button>

              {calculationResult && (
                <CalculationResults
                  result={calculationResult}
                  onSave={() => setShowSaveModal(true)}
                />
              )}
            </div>
          </div>
        </main>
      </div>

      {showSaveModal && (
        <SaveCalculationModal
          onSave={handleSaveCalculation}
          onClose={() => setShowSaveModal(false)}
        />
      )}
    </>
  );
};

export default PeptideCalculator;